/**
 * Push notification registration for the native shell.
 *
 * Server keeps a `push_devices` collection keyed by (user_email, token) so
 * we can broadcast to a specific user across devices when eSIM data usage
 * crosses a threshold or a plan expires. On the web this is a no-op.
 */

import axios from 'axios';
import { PushNotifications } from '@capacitor/push-notifications';
import { isNative, platform } from './platform';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let listenersRegistered = false;

/**
 * Call once after a user logs in successfully.
 * @param {string} userEmail
 */
export async function registerPushForUser(userEmail) {
  if (!isNative() || !userEmail) return { ok: false, reason: 'not-native' };

  try {
    const perm = await PushNotifications.checkPermissions();
    let allowed = perm.receive;
    if (allowed !== 'granted') {
      const r = await PushNotifications.requestPermissions();
      allowed = r.receive;
    }
    if (allowed !== 'granted') return { ok: false, reason: 'denied' };

    if (!listenersRegistered) {
      listenersRegistered = true;
      PushNotifications.addListener('registration', async (token) => {
        try {
          await axios.post(`${API}/user/push-device`, {
            email: userEmail,
            token: token.value,
            platform: platform(),
          });
        } catch (e) {
          // Non-fatal — user can still use the app.
          console.warn('push device register failed', e);
        }
      });
      PushNotifications.addListener('registrationError', (err) => {
        console.warn('push registration error', err);
      });
      // Foreground display — leave to the OS by default. Tap handling can
      // navigate deep into the app in future.
      PushNotifications.addListener('pushNotificationActionPerformed', (n) => {
        const url = n?.notification?.data?.deep_link;
        if (url && typeof url === 'string') {
          window.location.assign(url);
        }
      });
    }

    await PushNotifications.register();
    return { ok: true };
  } catch (e) {
    console.warn('push init failed', e);
    return { ok: false, reason: String(e) };
  }
}

/** Called on logout so we stop targeting the old user's device. */
export async function unregisterPushForUser(userEmail) {
  if (!isNative() || !userEmail) return;
  try {
    await axios.post(`${API}/user/push-device/unregister`, { email: userEmail });
  } catch (e) {
    // best-effort
  }
}
